import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// --- ANIMATIONS ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

// --- COLOR PALETTE ---
const colors = {
  primary: '#2c3e50',
  secondary: '#34495e',
  accent: '#FFC107',
  accentDark: '#E6AC00',
  success: '#28a745',
  danger: '#dc3545',
  backgroundLight: '#f9f9f9',
  backgroundWhite: '#ffffff',
  textBody: '#555',
  textHeader: '#333',
  darkBg: '#1a1a1a',
  shadowLight: 'rgba(0, 0, 0, 0.05)',
  shadowMedium: 'rgba(0, 0, 0, 0.1)',
  shadowHeavy: 'rgba(0, 0, 0, 0.15)',
};

const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '992px',
  large: '1200px',
};

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  font-family: 'Poppins', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: ${colors.textHeader};
  background: linear-gradient(135deg, #f8f9fa 0%, ${colors.backgroundWhite} 50%, #f5f7fa 100%);
  line-height: 1.6;
  animation: ${fadeInUp} 0.8s ease-out;
`;

// --- Common Styles for Sections ---
const Section = styled.section`
  padding: 80px 5%;
  position: relative;
  
  @media (max-width: ${breakpoints.tablet}) {
    padding: 60px 4%;
  }
`;

const CalloutBanner = styled.div`
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%);
  color: white;
  padding: 30px 5%;
  text-align: center;
  font-size: clamp(1.1em, 2.5vw, 1.4em);
  font-weight: 700;
  box-shadow: 0 10px 30px rgba(255, 193, 7, 0.3);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    animation: ${shimmer} 3s infinite;
  }
`;

const ActionButton = styled.a`
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%);
  color: ${colors.primary};
  padding: 16px 35px;
  border-radius: 12px;
  text-decoration: none;
  font-size: clamp(1em, 2vw, 1.1em);
  font-weight: 700;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: inline-block;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(255, 193, 7, 0.4);
  position: relative;
  overflow: hidden;
  animation: ${pulse} 2s infinite 1s;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.6s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 35px rgba(255, 193, 7, 0.5);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

// --- Voice Search Optimization Section ---
const VoiceSearchSection = styled(Section)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: ${float} 8s ease-in-out infinite;
  }
`;

const VoiceSearchText = styled.p`
  max-width: 1000px;
  margin: 0 auto 30px auto;
  line-height: 1.8;
  font-size: clamp(1em, 2.5vw, 1.2em);
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  font-weight: 400;
  position: relative;
  z-index: 2;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const CalloutBannerDark = styled.div`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  color: white;
  padding: 25px 5%;
  text-align: center;
  font-size: clamp(1.1em, 2.5vw, 1.3em);
  font-weight: 700;
  margin: 40px auto;
  border-radius: 15px;
  max-width: 800px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 2;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;

  &::before {
    content: '🎯';
    margin-right: 15px;
    font-size: 1.2em;
  }
`;

// --- Image Text Block Section ---
const ImageTextBlockSection = styled(Section)`
  display: flex;
  gap: 60px;
  align-items: center;
  background: ${colors.backgroundWhite};
  border-radius: 20px;
  margin: 40px 5%;
  box-shadow: 0 20px 60px ${colors.shadowMedium};
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: ${breakpoints.desktop}) {
    flex-direction: column;
    margin: 40px 4%;
    gap: 40px;
  }
`;

const LinkBlockImage = styled.img`
  width: 50%;
  max-width: 500px;
  height: 400px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 20px 60px ${colors.shadowHeavy};
  transition: all 0.5s ease;
  animation: ${slideInRight} 0.8s ease-out;

  &:hover {
    transform: scale(1.05) rotate(1deg);
    box-shadow: 0 25px 80px ${colors.shadowHeavy};
  }

  @media (max-width: ${breakpoints.desktop}) {
    width: 80%;
    height: 300px;
    order: -1;
  }

  @media (max-width: ${breakpoints.tablet}) {
    width: 100%;
    height: 250px;
  }
`;

const LinkBlockContent = styled.div`
  width: 50%;
  animation: ${slideInLeft} 0.8s ease-out;

  @media (max-width: ${breakpoints.desktop}) {
    width: 100%;
  }
`;

const LinkBlockHeader = styled.h2`
  font-size: clamp(2em, 4vw, 2.8em);
  color: ${colors.primary};
  margin-bottom: 25px;
  font-weight: 800;
  line-height: 1.2;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  padding-bottom: 15px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, ${colors.accent}, transparent);
    border-radius: 2px;
  }
`;

const LinkBlockParagraph = styled.p`
  line-height: 1.8;
  font-size: clamp(1em, 2vw, 1.1em);
  color: ${colors.textBody};
  margin-bottom: 25px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

// --- Did You Know Section (Stats) ---
const DidYouKnowSection = styled.section`
  background: linear-gradient(135deg, ${colors.darkBg} 0%, #2d3748 100%);
  color: white;
  padding: 100px 5%;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
  }
`;

const DidYouKnowTitle = styled.h2`
  font-size: clamp(2.2em, 5vw, 3em);
  margin-bottom: 60px;
  font-weight: 800;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, #ffffff 0%, ${colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.4s ease;
  animation: ${fadeInUp} 0.8s ease-out ${props => props.delay || '0s'} both;

  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const StatValue = styled.p`
  font-size: clamp(2.5em, 6vw, 3.5em);
  font-weight: 800;
  color: ${colors.accent};
  margin: 0;
  position: relative;
  padding-bottom: 20px;
  text-shadow: 0 5px 15px rgba(255, 193, 7, 0.3);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${colors.accent}, transparent);
  }
`;

const StatDescription = styled.p`
  font-size: clamp(0.95em, 2vw, 1.1em);
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin-top: 20px;
  text-align: center;
  font-weight: 500;
`;

// --- FAQ Section ---
const FaqSection = styled(Section)`
  background: ${colors.backgroundWhite};
  border-radius: 20px;
  margin: 40px 5%;
  box-shadow: 0 20px 60px ${colors.shadowMedium};
  
  @media (max-width: ${breakpoints.tablet}) {
    margin: 40px 4%;
  }
`;

const FaqTitle = styled.h2`
  font-size: clamp(2em, 4vw, 2.8em);
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  color: ${colors.primary};
  font-weight: 800;

  &::after {
    content: '';
    display: block;
    width: 100px;
    height: 5px;
    background: linear-gradient(90deg, ${colors.accent}, ${colors.accentDark});
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: -15px;
    border-radius: 3px;
    animation: ${pulse} 2s infinite;
  }
`;

const FaqItemContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const FaqItem = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 25px 0;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out ${props => props.delay || '0s'} both;

  &:hover {
    background: rgba(255, 193, 7, 0.02);
    border-radius: 10px;
    padding: 25px 15px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-size: clamp(1.1em, 2.5vw, 1.2em);
  font-weight: 700;
  color: ${colors.primary};
  padding: 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.accentDark};
  }

  &:focus {
    outline: none;
  }
`;

const FaqIcon = styled.span`
  font-size: 1.8em;
  color: ${props => (props.isOpen ? colors.danger : colors.success)};
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: rotate(${props => (props.isOpen ? '45deg' : '0deg')});
  background: ${props => (props.isOpen ? 'rgba(220, 53, 69, 0.1)' : 'rgba(40, 167, 69, 0.1)')};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FaqAnswer = styled.div`
  font-size: clamp(1em, 2vw, 1.1em);
  color: ${colors.textBody};
  padding: ${props => (props.isOpen ? '20px 0 10px 0' : '0')};
  line-height: 1.8;
  max-height: ${props => (props.isOpen ? '1000px' : '0')};
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-left: ${props => (props.isOpen ? `3px solid ${colors.accent}` : '3px solid transparent')};
  padding-left: ${props => (props.isOpen ? '20px' : '0')};
`;

// --- Follow Us Section ---
const FollowUsBanner = styled.div`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  color: white;
  padding: 60px 5%;
  text-align: center;
  margin: 80px 0 0 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, ${colors.accent}, ${colors.accentDark}, ${colors.accent});
    background-size: 200% 100%;
    animation: ${gradientShift} 3s ease infinite;
  }
`;

const FollowUsButton = styled(ActionButton)`
  background: transparent;
  border: 2px solid white;
  color: white;
  margin: 0 10px;
  padding: 12px 25px;
  
  &:hover {
    background: white;
    color: ${colors.primary};
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(255, 255, 255, 0.3);
  }
`;

const SocialButtonsContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
`;

// --- DATA ---
const faqData = [
    {
        q: 'What are custom SEO services and how do they differ from standard SEO packages?',
        a: 'Custom SEO services are tailored strategies developed specifically for your business, industry, target audience, and current digital presence. Unlike standard packages, which offer generic, fixed tasks, custom services involve in-depth audits, specialized keyword research, bespoke content plans, and personalized backlinking efforts, ensuring maximum ROI and long-term results.',
    },
    {
        q: 'How does SRK Software integrate custom SEO services into pre-packaged deliverables?',
        a: 'At SRK Software, we use "pre-packaged deliverables" as a starting framework. We then integrate custom elements—like specialized technical audits, industry-specific content briefs, and unique outreach strategies—into this framework. This gives you the efficiency of a package with the specialized focus of a custom solution.',
    },
    {
        q: 'How do you determine the right custom SEO strategy for my business?',
        a: 'We start with a comprehensive 360-degree audit covering your technical SEO health, content gaps, and backlink profile. We also perform thorough competitor analysis and listen to your specific business goals to craft a strategy that addresses your unique challenges and capitalizes on high-potential opportunities.',
    },
    {
        q: 'What specific SEO techniques and tactics are included in custom services?',
        a: 'Our custom services may include advanced schema markup implementation, customized Core Web Vitals optimization, programmatic SEO, international SEO (if applicable), sophisticated link-earning campaigns, and tailored content funnels designed to target specific buying stages, going far beyond basic on-page and off-page efforts.',
    },
    {
        q: 'Can custom SEO services accommodate my budget and business size?',
        a: 'Absolutely. Customization means flexibility. We scale the scope of the work—from the depth of the audit to the volume of content and links—to align perfectly with your available budget and the scale of your business, ensuring every dollar spent contributes directly to your highest priority goals.',
    },
    {
        q: 'How long does it take to see results with custom SEO?',
        a: 'While custom SEO accelerates results compared to standard methods, search engine results are not instant. Typically, clients begin seeing significant improvements in traffic and rankings within 3 to 6 months, with the maximum competitive advantage realized over 9 to 12 months as authority builds.',
    },
    {
        q: 'Will I receive regular progress reports with custom SEO services?',
        a: 'Yes. Transparency is a core value at SRK Software. You will receive detailed monthly reports that not only track key performance indicators (KPIs) like rankings, traffic, and conversions but also provide deep analysis and insights into the specific activities performed and the strategies planned for the next cycle.',
    },
    {
        q: 'How do I get started with custom SEO services for my business?',
        a: 'Getting started is easy. Simply click the "Click Here" or "Get Started" button on our page, or contact us directly. We will schedule a free consultation to understand your business needs and recommend the ideal custom SEO strategy to achieve your goals.',
    },
];

// --- COMPONENTS ---
const FaqAccordion = ({ data }) => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFaq = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <FaqItemContainer>
            {data.map((item, index) => (
                <FaqItem key={index} delay={`${0.1 + index * 0.1}s`}>
                    <FaqQuestion onClick={() => toggleFaq(index)}>
                        {item.q}
                        <FaqIcon isOpen={openIndex === index}>
                            {openIndex === index ? '✕' : '+'}
                        </FaqIcon>
                    </FaqQuestion>
                    <FaqAnswer isOpen={openIndex === index}>
                        {item.a}
                    </FaqAnswer>
                </FaqItem>
            ))}
        </FaqItemContainer>
    );
};

const CustomSEO3 = () => {
    return (
        <PageContainer>
            {/* 1. Voice Search Optimization Section */}
            <VoiceSearchSection>
                <VoiceSearchText>
                    **Voice Search Optimization** is about making your online content compatible with how people naturally speak instead of typing keywords. Users ask questions about voice assistants like Siri or Google Assistant. To optimize for voice search, use conversational language in your content, structure answers clearly, and target long-tail, question-based keywords.
                </VoiceSearchText>
                <VoiceSearchText>
                    Voice Search is growing rapidly, so adapting your content to this trend can significantly improve your visibility and drive more leads, especially on mobile devices where voice search is most prevalent.
                </VoiceSearchText>
                <CalloutBannerDark>
                    Make yourself the focal point of any visitor's experience!
                </CalloutBannerDark>
                <ActionButton href="#click-here" style={{zIndex: 2, position: 'relative'}}>
                    Optimize for Voice Search ➔
                </ActionButton>
            </VoiceSearchSection>

            {/* 2. Link Building Text/Image Block */}
            <ImageTextBlockSection>
                <LinkBlockContent>
                    <LinkBlockHeader>Strategic Link Building</LinkBlockHeader>
                    <LinkBlockParagraph>
                        **Link building** means creating valuable connections across the internet. It's about earning high-quality backlinks from other reputable websites to yours. These links act like digital votes of confidence, telling search engines like Google that your site is trustworthy, authoritative, and relevant.
                    </LinkBlockParagraph>
                    <LinkBlockParagraph>
                        Search engines consider these endorsements when ranking websites. Quality always trumps quantity—a single link from an industry-leading site carries more weight than dozens from low-quality sources.
                    </LinkBlockParagraph>
                    <LinkBlockParagraph>
                        Strategic link building significantly increases your website's domain authority and can dramatically improve search result rankings. While it requires time and expertise, it remains an essential component of comprehensive SEO success.
                    </LinkBlockParagraph>
                    <ActionButton href="#view-results">See Our Link Building Results ➔</ActionButton>
                </LinkBlockContent>
                <LinkBlockImage 
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" 
                    alt="Link Building Strategy Visualization" 
                />
            </ImageTextBlockSection>

            {/* 3. Did You Know Section (Stats) */}
            <DidYouKnowSection>
                <DidYouKnowTitle>Did You Know These SEO Insights?</DidYouKnowTitle>
                <StatsGrid>
                    <StatItem delay="0.1s">
                        <StatValue>0.78%</StatValue>
                        <StatDescription>
                            of Google searchers click on results from the second page and beyond
                        </StatDescription>
                    </StatItem>
                    <StatItem delay="0.2s">
                         <StatValue>93%</StatValue>
                        <StatDescription>
                            of all online experiences begin with search engine queries
                        </StatDescription>
                    </StatItem>
                    <StatItem delay="0.3s">
                         <StatValue>63%</StatValue>
                        <StatDescription>
                            of consumers conduct thorough online research before making purchasing decisions
                        </StatDescription>
                    </StatItem>
                    <StatItem delay="0.4s">
                         <StatValue>61%</StatValue>
                        <StatDescription>
                            of marketing professionals prioritize SEO and organic search presence above other channels
                        </StatDescription>
                    </StatItem>
                </StatsGrid>
            </DidYouKnowSection>

            {/* 4. Frequently Asked Questions Section */}
            <FaqSection>
                <FaqTitle>Frequently Asked Questions</FaqTitle>
                <FaqAccordion data={faqData} />
            </FaqSection>

            {/* 5. Follow Us Banner */}
            <FollowUsBanner>
                <h3 style={{fontSize: 'clamp(1.8em, 4vw, 2.5em)', margin: '0 0 20px 0', fontWeight: 700}}>
                    Stay Connected With SRK Software
                </h3>
                <p style={{fontSize: 'clamp(1em, 2.5vw, 1.2em)', marginBottom: '30px', opacity: 0.9}}>
                    Follow us for the latest SEO insights, tips, and industry updates
                </p>
                <SocialButtonsContainer>
                    <FollowUsButton href="#facebook">📘 Facebook</FollowUsButton>
                    <FollowUsButton href="#twitter">🐦 Twitter</FollowUsButton>
                    <FollowUsButton href="#linkedin">💼 LinkedIn</FollowUsButton>
                    <FollowUsButton href="#instagram">📷 Instagram</FollowUsButton>
                </SocialButtonsContainer>
            </FollowUsBanner>
        </PageContainer>
    );
};

export default CustomSEO3;